import React, { useState, useEffect } from 'react';
import { FaPencilAlt, FaBook, FaRegClock, FaLanguage, FaStar, FaImage, FaListOl, FaChartLine, FaUpload, FaEye, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SummaryApi from "../common/index";

const CourseManagement = () => {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    category_id: '',
    course_title: '',
    subtitle: '',
    description: '',
    course_duration: '',
    total_lessions: '',
    diffculty_level: '',
    language_instruction: '',
    rating: '',
    course_image1: null,
    course_image2: null,
    is_active: false,
    is_trendy_course: false,
    is_career_course: false,
  });
  const [imagePreview1, setImagePreview1] = useState(null);
  const [imagePreview2, setImagePreview2] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Helper function to format image URLs
  const formatImageUrl = (imagePath) => {
    if (!imagePath) return '';
    
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    if (imagePath instanceof File) {
      return URL.createObjectURL(imagePath);
    }
    
    const baseUrl = SummaryApi.Images.url.replace('/api', '');
    
    if (imagePath.includes(baseUrl)) {
      return imagePath;
    }
    
    if (imagePath.startsWith('uploads/')) {
      return `${baseUrl}/${imagePath}`;
    }
    
    return `${baseUrl}/${imagePath}`;
  };

  // Fetch courses and categories on mount
  useEffect(() => {
    fetchCourses();
    fetchCategories();
    const queryParams = new URLSearchParams(window.location.search);
    const id = queryParams.get('id');
    if (id) {
      fetchCourseById(id);
    }
  }, []);

  const fetchCourses = () => {
    fetch(SummaryApi.CourseList.url, { method: SummaryApi.CourseList.method })
      .then(res => res.json())
      .then(data => {
        if (data.status && Array.isArray(data.data)) {
          const updatedCourses = data.data.map(course => ({
            ...course,
            course_image1: formatImageUrl(course.course_image1),
            course_image2: formatImageUrl(course.course_image2),
            is_active: course.is_active === 1,
            is_trendy_course: course.is_trendy_course === 1,
            is_career_course: course.is_career_course === 1,
            course_duration: course.course_duration ? course.course_duration.replace(' weeks', '') : '',
          }));
          setCourses(updatedCourses);
        }
      })
      .catch(err => {
        console.error("Error fetching courses:", err);
        toast.error("Failed to fetch courses");
      });
  };

  const fetchCategories = () => {
    fetch(SummaryApi.fetchcategorynames.url, { method: SummaryApi.fetchcategorynames.method })
      .then(res => res.json())
      .then(data => {
        if (data.status && Array.isArray(data.data)) {
          setCategories(data.data);
        }
      })
      .catch(err => {
        console.error("Error fetching categories:", err);
        toast.error("Failed to fetch categories");
      });
  };

  const fetchCourseById = async (id) => {
    try {
      const response = await fetch(SummaryApi.Coursebyid.url, {
        method: SummaryApi.Coursebyid.method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ course_id: id }),
      });
      const data = await response.json();
      if (data.status && data.data) {
        const course = data.data;
        setFormData({
          category_id: course.category_id || '',
          course_title: course.course_title || '',
          subtitle: course.subtitle || '',
          description: course.description || '',
          course_duration: course.course_duration ? course.course_duration.replace(' weeks', '') : '',
          total_lessions: course.total_lessions || '',
          diffculty_level: course.diffculty_level || '',
          language_instruction: course.language_instruction || '',
          rating: course.rating || '',
          course_image1: formatImageUrl(course.course_image1),
          course_image2: formatImageUrl(course.course_image2),
          is_active: course.is_active === 1,
          is_trendy_course: course.is_trendy_course === 1,
          is_career_course: course.is_career_course === 1,
        });
        setImagePreview1(formatImageUrl(course.course_image1));
        setImagePreview2(formatImageUrl(course.course_image2));
        setEditingId(id);
      } else {
        toast.error(data.message || 'Failed to fetch course details');
      }
    } catch (err) {
      console.error("Error fetching course by id:", err);
      toast.error("Failed to fetch course details");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleImageChange = (e, imageNumber) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (imageNumber === 1) {
          setImagePreview1(reader.result);
          setFormData({
            ...formData,
            course_image1: file
          });
        } else {
          setImagePreview2(reader.result);
          setFormData({
            ...formData,
            course_image2: file
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (imageNumber) => {
    if (imageNumber === 1) {
      setImagePreview1(null);
      setFormData({
        ...formData,
        course_image1: null
      });
    } else {
      setImagePreview2(null);
      setFormData({
        ...formData,
        course_image2: null
      });
    }
  };

  const toggleActive = () => {
    setFormData(prev => ({
      ...prev,
      is_active: !prev.is_active
    }));
  };

  const toggleTrendyCourse = () => {
    setFormData(prev => ({
      ...prev,
      is_trendy_course: !prev.is_trendy_course,
      course_duration: !prev.is_trendy_course ? '12' : ''
    }));
  };

  const toggleCareerCourse = () => {
    setFormData(prev => ({
      ...prev,
      is_career_course: !prev.is_career_course
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const submitData = new FormData();
    submitData.append('category_id', formData.category_id);
    submitData.append('course_title', formData.course_title);
    submitData.append('subtitle', formData.subtitle);
    submitData.append('description', formData.description);
    submitData.append('course_duration', formData.course_duration ? `${formData.course_duration} weeks` : '');
    submitData.append('total_lessions', formData.total_lessions);
    submitData.append('diffculty_level', formData.diffculty_level);
    submitData.append('language_instruction', formData.language_instruction);
    submitData.append('rating', formData.rating);
    submitData.append('is_active', formData.is_active ? 1 : 0);
    submitData.append('is_trendy_course', formData.is_trendy_course ? 1 : 0);
    submitData.append('is_career_course', formData.is_career_course ? 1 : 0);

    if (formData.course_image1 instanceof File) {
      submitData.append('course_image1', formData.course_image1);
    } else if (typeof formData.course_image1 === 'string' && formData.course_image1) {
      const filename = formData.course_image1.split('/').pop();
      submitData.append('course_image1', filename);
    }

    if (formData.course_image2 instanceof File) {
      submitData.append('course_image2', formData.course_image2);
    } else if (typeof formData.course_image2 === 'string' && formData.course_image2) {
      const filename = formData.course_image2.split('/').pop();
      submitData.append('course_image2', filename);
    }

    if (editingId) {
      submitData.append('course_id', editingId);

      fetch(SummaryApi.Courseupdate.url, {
        method: SummaryApi.Courseupdate.method,
        body: submitData,
      })
        .then(res => res.json())
        .then(data => {
          setIsSubmitting(false);
          if (data.status) {
            toast.success("Course updated successfully!");
            fetchCourseById(editingId);
            fetchCourses();
          } else {
            console.error("Update failed:", data.message);
            toast.error(data.message || "Failed to update course");
          }
        })
        .catch(err => {
          setIsSubmitting(false);
          console.error("Error updating course:", err);
          toast.error("Error updating course");
        });
    } else {
      fetch(SummaryApi.Coursecreate.url, {
        method: SummaryApi.Coursecreate.method,
        body: submitData,
      })
        .then(res => res.json())
        .then(data => {
          setIsSubmitting(false);
          if (data.status) {
            toast.success("Course created successfully!");
            resetForm();
            fetchCourses();
          } else {
            console.error("Creation failed:", data.message);
            toast.error(data.message || "Failed to create course");
          }
        })
        .catch(err => {
          setIsSubmitting(false);
          console.error("Error creating course:", err);
          toast.error("Error creating course");
        });
    }
  };

  const handleEdit = (course) => {
    setFormData({
      category_id: course.category_id,
      course_title: course.course_title,
      subtitle: course.subtitle,
      description: course.description,
      course_duration: course.course_duration ? course.course_duration.replace(' weeks', '') : '',
      total_lessions: course.total_lessions,
      diffculty_level: course.diffculty_level,
      language_instruction: course.language_instruction,
      rating: course.rating,
      course_image1: course.course_image1,
      course_image2: course.course_image2,
      is_active: course.is_active,
      is_trendy_course: course.is_trendy_course,
      is_career_course: course.is_career_course,
    });
    setEditingId(course.course_id);
    
    if (course.course_image1) {
      setImagePreview1(course.course_image1);
    }
    if (course.course_image2) {
      setImagePreview2(course.course_image2);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      course_title: '',
      subtitle: '',
      description: '',
      course_duration: '',
      total_lessions: '',
      diffculty_level: '',
      language_instruction: '',
      rating: '',
      course_image1: null,
      course_image2: null,
      is_active: false,
      is_trendy_course: false,
      is_career_course: false,
    });
    setImagePreview1(null);
    setImagePreview2(null);
    setEditingId(null);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.category_id === categoryId);
    return category ? category.category_name : 'Unknown Category';
  };

  return (
    <div className="bg-gradient-to-br p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 sm:mb-8 flex justify-between items-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Course Management</h1>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5 sm:p-6 mb-6 sm:mb-8 border-2 border-navyblue">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
            {editingId ? 'Update Course' : 'Create New Course'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* Category Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  className="block w-full border rounded-md p-2 border-gray-300 focus:border-navyblue focus:ring-2 focus:ring-navyblue"
                  required
                >
                  <option value="">-- Select Category --</option>
                  {categories.map(category => (
                    <option key={category.category_id} value={category.category_id}>
                      {category.category_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Course Title */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Course Title</label>
                <div className="relative">
                  <FaBook className="absolute left-2 top-3.5 text-navyblue" />
                  <input 
                    type="text" 
                    name="course_title" 
                    value={formData.course_title} 
                    onChange={handleChange} 
                    className="block w-full border rounded-md p-2 pl-8 border-gray-300 focus:border-navyblue focus:ring-2 focus:ring-navyblue"
                    required 
                  />
                </div>
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                <input 
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleChange}
                  className="block w-full border rounded-md p-2 border-gray-300 focus:border-navyblue focus:ring-2 focus:ring-navyblue"
                  required
                />
              </div>

              {/* Course Duration */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Course Duration</label>
                <div className="relative flex items-center">
                  <FaRegClock className="absolute left-2 top-3.5 text-navyblue" />
                  <input 
                    type="number" 
                    name="course_duration" 
                    value={formData.course_duration} 
                    onChange={handleChange} 
                    className="block w-full border rounded-md p-2 pl-8 border-gray-300 focus:border-navyblue focus:ring-2 focus:ring-navyblue"
                    required 
                  />
                  <span className="ml-2 text-sm text-gray-700">weeks</span>
                </div>
              </div>

              {/* Total Lessons */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Lessons</label>
                <div className="relative">
                  <FaListOl className="absolute left-2 top-3.5 text-navyblue" />
                  <input 
                    type="number" 
                    name="total_lessions" 
                    value={formData.total_lessions} 
                    onChange={handleChange} 
                    className="block w-full border rounded-md p-2 pl-8 border-gray-300 focus:border-navyblue focus:ring-2 focus:ring-navyblue"
                    required 
                  />
                </div>
              </div>

              {/* Difficulty Level */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty Level</label>
                <div className="relative">
                  <FaChartLine className="absolute left-2 top-3.5 text-navyblue" />
                  <select
                    name="diffculty_level"
                    value={formData.diffculty_level}
                    onChange={handleChange}
                    className="block w-full border rounded-md p-2 pl-8 border-gray-300 focus:border-navyblue focus:ring-2 focus:ring-navyblue"
                    required
                  >
                    <option value="">-- Select Level --</option>
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
              </div>

              {/* Language of Instruction */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Language of Instruction</label>
                <div className="relative">
                  <FaLanguage className="absolute left-2 top-3.5 text-navyblue" />
                  <input 
                    type="text" 
                    name="language_instruction" 
                    value={formData.language_instruction} 
                    onChange={handleChange} 
                    className="block w-full border rounded-md p-2 pl-8 border-gray-300 focus:border-navyblue focus:ring-2 focus:ring-navyblue"
                    required 
                  />
                </div>
              </div>

              {/* Rating */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                <div className="relative">
                  <FaStar className="absolute left-2 top-3.5 text-navyblue" />
                  <input 
                    type="number" 
                    name="rating" 
                    value={formData.rating} 
                    onChange={handleChange} 
                    className="block w-full border rounded-md p-2 pl-8 border-gray-300 focus:border-navyblue focus:ring-2 focus:ring-navyblue"
                    step="0.1"
                    min="0"
                    max="5"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea 
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className="block w-full border rounded-md p-2 border-gray-300 focus:border-navyblue focus:ring-2 focus:ring-navyblue"
                />
              </div>

              {/* Course Image 1 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Course Image 1</label>
                {imagePreview1 || formData.course_image1 ? (
                  <div className="relative mt-1">
                    <label className="cursor-pointer">
                      <img 
                        src={imagePreview1 || formatImageUrl(formData.course_image1)} 
                        alt="Preview" 
                        className="h-16 w-16 object-cover rounded-md"
                      />
                      <input 
                        type="file" 
                        className="hidden" 
                        onChange={(e) => handleImageChange(e, 1)}
                        accept="image/*"
                      />
                    </label>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-navyblue">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <FaUpload className="w-8 h-8 mb-3 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500">Click to upload</p>
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      onChange={(e) => handleImageChange(e, 1)}
                      accept="image/*"
                    />
                  </label>
                )}
              </div>

              {/* Course Image 2 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Course Image 2</label>
                {imagePreview2 || formData.course_image2 ? (
                  <div className="relative mt-1">
                    <label className="cursor-pointer">
                      <img 
                        src={imagePreview2 || formatImageUrl(formData.course_image2)} 
                        alt="Preview" 
                        className="h-16 w-16 object-cover rounded-md"
                      />
                      <input 
                        type="file" 
                        className="hidden" 
                        onChange={(e) => handleImageChange(e, 2)}
                        accept="image/*"
                      />
                    </label>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-navyblue">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <FaUpload className="w-8 h-8 mb-3 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500">Click to upload</p>
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      onChange={(e) => handleImageChange(e, 2)}
                      accept="image/*"
                    />
                  </label>
                )}
              </div>

              {/* Active Toggle */}
              <div className="flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={toggleActive}
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 ${formData.is_active ? 'bg-green-500' : 'bg-red-500'} peer-focus:outline-none rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${formData.is_active ? 'peer-checked:after:translate-x-full' : ''}`} />
                  <span className="ml-3 text-sm font-medium text-gray-900">
                    Active
                  </span>
                </label>
              </div>

              {/* Trendy Course Toggle */}
              <div className="flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_trendy_course"
                    checked={formData.is_trendy_course}
                    onChange={toggleTrendyCourse}
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 ${formData.is_trendy_course ? 'bg-green-500' : 'bg-red-500'} peer-focus:outline-none rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${formData.is_trendy_course ? 'peer-checked:after:translate-x-full' : ''}`} />
                  <span className="ml-3 text-sm font-medium text-gray-900">
                    Trendy Course
                  </span>
                </label>
              </div>

              {/* Career Course Toggle */}
              <div className="flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_career_course"
                    checked={formData.is_career_course}
                    onChange={toggleCareerCourse}
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 ${formData.is_career_course ? 'bg-green-500' : 'bg-red-500'} peer-focus:outline-none rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${formData.is_career_course ? 'peer-checked:after:translate-x-full' : ''}`} />
                  <span className="ml-3 text-sm font-medium text-gray-900">
                    Career Course
                  </span>
                </label>
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <button 
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-3 rounded-md text-white font-medium bg-navyblue hover:bg-blue-900 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (editingId ? 'Updating...' : 'Creating...') : (editingId ? 'Update Course' : 'Create Course')}
              </button>
            </div>
          </form>
        </div>

      </div>
      <style>{`
        .border-navyblue {
          border-color: #001f3f;
        }
        .text-navyblue {
          color: #001f3f;
        }
        .bg-navyblue {
          background-color: #001f3f;
        }
        .hover\\:bg-blue-900:hover {
          background-color: #001a33;
        }
        .hover\\:text-blue-900:hover {
          color: #001a33;
        }
      `}</style>
    </div>
  );
};

export default CourseManagement;